export type MongoRawQuery = {
  method: 'find' | 'aggregate' | 'count' | 'insert' | 'update' | 'delete'
  filter?: Record<string, unknown>
  projection?: Record<string, unknown>
  sort?: Record<string, unknown>
  limit?: number
  skip?: number
  pipeline?: Record<string, unknown>[]
  /** Insert document (`method: "insert"`). */
  data?: Record<string, unknown>
  /** Update document (`method: "update"`) — bare fields are wrapped as `$set`. */
  update?: Record<string, unknown>
}

const RAW_METHODS = new Set<MongoRawQuery['method']>([
  'find', 'aggregate', 'count', 'insert', 'update', 'delete',
])

export function parseMongoRawQuery(raw: string): MongoRawQuery {
  const parsed = JSON.parse(raw || '{}') as Record<string, unknown>
  let method: MongoRawQuery['method']
  if (typeof parsed.method === 'string' && RAW_METHODS.has(parsed.method as MongoRawQuery['method'])) {
    method = parsed.method as MongoRawQuery['method']
  } else if (Array.isArray(parsed.pipeline)) {
    // pipeline without method → aggregate (find would strip pipeline)
    method = 'aggregate'
  } else {
    method = 'find'
  }
  const { method: _m, ...rest } = parsed
  return { method, ...rest } as MongoRawQuery
}

function isNonEmptyObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0
}

export function buildMongoQueryFromRaw(
  resource: string,
  raw: string,
): { method: string; args: Record<string, unknown> } {
  const parsed = parseMongoRawQuery(raw)

  if (parsed.method === 'aggregate') {
    return {
      method: 'aggregate',
      args: { collection: resource, pipeline: parsed.pipeline ?? [] },
    }
  }

  if (parsed.method === 'count') {
    return {
      method: 'count',
      args: { collection: resource, filter: parsed.filter },
    }
  }

  if (parsed.method === 'insert') {
    if (!isNonEmptyObject(parsed.data)) {
      throw new Error('Insert requires a non-empty "data" document')
    }
    return {
      method: 'insert_one',
      args: { collection: resource, data: parsed.data },
    }
  }

  if (parsed.method === 'update') {
    if (!isNonEmptyObject(parsed.filter)) {
      throw new Error('Update requires a non-empty "filter"')
    }
    if (!isNonEmptyObject(parsed.update)) {
      throw new Error('Update requires a non-empty "update" document')
    }
    // Allow bare fields as $set shorthand (same as the visual builder)
    const hasOperator = Object.keys(parsed.update).some(k => k.startsWith('$'))
    const updateDoc = hasOperator ? parsed.update : { $set: parsed.update }
    const setVal = (updateDoc as Record<string, unknown>).$set
    if (
      Object.keys(updateDoc).length === 1
      && setVal && typeof setVal === 'object' && !Array.isArray(setVal)
      && !Object.keys(setVal as object).length
    ) {
      throw new Error('Update $set is empty — add at least one field')
    }
    return {
      method: 'update_many',
      args: { collection: resource, filter: parsed.filter, update: updateDoc },
    }
  }

  if (parsed.method === 'delete') {
    if (!isNonEmptyObject(parsed.filter)) {
      throw new Error('Delete requires a non-empty "filter"')
    }
    return {
      method: 'delete_many',
      args: { collection: resource, filter: parsed.filter },
    }
  }

  const { method: _method, pipeline: _pipeline, data: _data, update: _update, ...findArgs } = parsed
  return {
    method: 'find',
    args: { collection: resource, ...findArgs },
  }
}
